import { readable } from 'svelte/store';
import type { Readable } from 'svelte/store';

/**
 * Page Visibility Store
 *
 * This store returns a Boolean value to indicate if the app page is visible. We use a combination
 * of the Page Visiblity API (https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API)
 * and the blur/focus browser events to handle various visibility change situations.
 *
 * Page Visiblity API: tracks visibility between browser tabs or minimizes the browser
 * focus/blur: tracks visibility when browser is backgrounded/foregrounded (i.e. switches windows)
 *
 * One gotcha is that the focus/blur events do not necessarily indicate that your browser is hidden.
 * A good example of this is opening DevTools and clicking into it.  It will send a blur event, even
 * though your page is not hidden.  `document.visibilityState` *seems* like it *should* work, but
 * for some reason it always returns 'visible' even if switching windows.  Shouldn't be a problem
 * for regular users, but might make debugging a bit tricky.
 *
 * This store assumes `document.hidden` is available, which has been supported by most browsers for
 * some time now (https://caniuse.com/?search=document.hidden).  The Page Visibility API example
 * includes some code for detecting vendor-specific `hidden` properties if we find those are needed.
 * https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API#example
 */

let pageVisibilityStore: Readable<boolean> | null = null;

const createNoopPageVisibilityStore = (): Readable<boolean> => {
    return readable(true, () => {});
};

const createPageVisibilityStore = (): Readable<boolean> => {
    return readable(true as boolean, (set) => {
        const handleVisibilityChange = () => set(!document.hidden);
        const handleBlurChange = () => set(false);
        const handleFocusChange = () => set(true);

        document.addEventListener(
            'visibilitychange',
            handleVisibilityChange,
            false,
        );
        window.addEventListener('blur', handleBlurChange);
        window.addEventListener('focus', handleFocusChange);

        // Set initial store value
        handleVisibilityChange();

        return function (): void {
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
                false,
            );
            window.removeEventListener('blur', handleBlurChange);
            window.removeEventListener('focus', handleFocusChange);
        };
    });
};

export const getPageVisibilityStore = (): Readable<boolean> => {
    if (typeof window === 'undefined' && pageVisibilityStore === null) {
        pageVisibilityStore = createNoopPageVisibilityStore();
    } else if (typeof window !== 'undefined' && pageVisibilityStore === null) {
        pageVisibilityStore = createPageVisibilityStore();
    }

    return pageVisibilityStore;
};
